/* eslint-disable no-bitwise */
const queryStringFromObject = obj => {
  const str = [];
  for (const property in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, property)) {
      if (obj[property].hasOwnProperty(constructor) && obj[property].constructor === Array) {
        for (const subProperty of obj[property]) {
          str.push(`${encodeURIComponent(property)}=${encodeURIComponent(subProperty)}`);
        }
      } else {
        const value =
          typeof obj[property] === 'undefined' || obj[property] === null ? '' : obj[property];
        str.push(`${encodeURIComponent(property)}=${encodeURIComponent(value)}`);
      }
    }
  }

  return str.join('&');
};

const isEmptyString = str => typeof str === 'undefined' || str === null || str.length === 0;

const leftPad = (paddingChar, padCount, str) =>
  `${paddingChar.repeat(padCount)}${str || ''}`.slice(-padCount);

const camelToSnake = str => {
  let newStr = str.replace(
    /([A-Z])/g,
    (originalString, firstCharacter) => `_${firstCharacter.toLowerCase()}`
  );

  if (newStr.slice(0, 1) === '_') {
    newStr = newStr.slice(1);
  }

  return newStr;
};

function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const formatPrice = (price, decimals = 2, shouldRemoveDecimalIfInteger = true) => {
  if (isEmptyString(price)) {
    return '$0';
  }

  let decimalsOverride = decimals;

  if (shouldRemoveDecimalIfInteger && Number.isInteger(Number.parseFloat(price))) {
    decimalsOverride = 0;
  }

  if (decimalsOverride) {
    return `\$${numberWithCommas(parseFloat(price).toFixed(decimalsOverride))}`;
  }

  return `\$${numberWithCommas(price)}`;
};

const formatPercentage = decimalFraction => {
  const integerPercentage = decimalFraction * 100;
  return `${integerPercentage}%`;
};

const trimTo = (str, maxLen) => {
  if (str.length <= maxLen) return str;

  return `${str.substring(0, maxLen - 3)}...`;
};

const setCharAt = (str, index, char) => {
  if (index > str.length - 1) return str;
  return str.substring(0, index) + char + str.substring(index + 1);
};

/**
 * Pluralize a word. E.g.:
 * pluralize(1, 'lay') => 'lay'
 * pluralize(2, 'egg') => 'eggs'
 * pluralize(3, 'hatch', 'es') => 'hatches'
 * pluralize(4, 'octopus', 'i', '2') => 'octopi'
 * @param {number} number
 * @param {string} word
 * @param {string} postfix
 * @param {number} trim
 * @return {string}
 */
const pluralize = (number, word, postfix = 's', trim = 0) => {
  if (word === 'is') {
    if (number === 1) {
      return 'is';
    }

    return 'are';
  }

  if (word === 'has') {
    if (number === 1) {
      return 'has';
    }

    return 'have';
  }

  if (word === 'person') {
    if (number === 1) {
      return 'person';
    }

    return 'people';
  }

  if (number === 1) {
    return word;
  }

  let pluralized = word;
  if (trim > 0) {
    pluralized = pluralized.substring(0, pluralized.length - trim);
  }

  pluralized += postfix;
  return pluralized;
};

const arrayToEnglish = arr => {
  if (arr.length === 0) {
    return '';
  }

  if (arr.length === 1) {
    return arr[0];
  }

  if (arr.length === 2) {
    return arr.join(' and ');
  }

  const last = arr.slice(-1);
  const first = arr.slice(0, -1).join(', ');
  return [first, last].join(', and ');
};

const objectToEnglish = obj => {
  const arr = [];
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      arr.push(obj[key]);
    }
  }

  return arrayToEnglish(arr);
};

/**
 * Strip all non numeric characters from a string.
 *
 * @param string {String}
 * @returns string {String} Only numeric characters
 */
const stripNonNumeric = string => {
  switch (typeof string) {
    case 'string':
      return string.replace(/[^\d]/g, '');
    case 'number':
      return String(string);
    default:
      return '';
  }
};

/**
 * Get first and last initials separated by specified separator.
 *
 * e.g John Doe => J D
 * @param {String} fullName
 * @param {String} separator
 * @returns {string}
 */
const getNameInitialsFromFullName = (fullName, separator) => {
  if (isEmptyString(fullName)) {
    return '';
  }

  return [fullName.split(' ')[0].slice(0, 1), fullName.split(' ')[1].slice(0, 1)].join(separator);
};

/**
 * Capitalize the first character of the given string.
 *
 * Optionally, lowercase the rest of the string
 * @param {string} string
 * @param {boolean} lowercaseTheRest
 * @returns {string}
 */
const capitalize = (string, lowercaseTheRest = false) => {
  if (isEmptyString(string)) {
    return '';
  }

  const firstChar = string.charAt(0);
  const theRest = string.slice(1);

  return firstChar.toUpperCase() + (lowercaseTheRest ? theRest.toLowerCase() : theRest);
};

/**
 * Capital case a sentence.
 * @param {string} sentence
 * @returns {string}
 */
const capitalCase = sentence =>
  typeof sentence === 'string'
    ? sentence
        .split(/\s+/)
        .map(word => capitalize(word, true))
        .join(' ')
    : '';

/**
 * Is the string made up of only numbers.
 * @param {string} string
 * @returns {boolean}
 */
const isNumericString = string => /^\d+$/.test(string);

const formObjectToSlackPost = form => {
  const fields = [];

  for (const key of Object.keys(form)) {
    fields.push(`*${capitalize(key)}:* ${form[key]}`);
  }

  return `${fields.join('\n')}`;
};

const splitFullName = name => {
  const splitName = { first: null, last: null };

  const indexOfFirstSpace = name.indexOf(' ');

  if (indexOfFirstSpace !== -1) {
    splitName.first = name.slice(0, indexOfFirstSpace);
    splitName.last = name.slice(indexOfFirstSpace + 1);
  } else {
    splitName.first = name;
  }

  return splitName;
};

const sentenceCase = input => {
  if (isEmptyString(input)) {
    return null;
  }

  const str = input.toString();
  return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

const toUpperCase = string =>
  string
    .split('')
    .map(c => c.toUpperCase())
    .join('');

const isValidZipCode = string => string && isNumericString(string) && string.length === 5;

const firstNCharacters = (string, n, addPeriod = false) =>
  `${string.slice(0, n)}${addPeriod ? '.' : ''}`;

const formatPhone = phoneString => {
  if (!phoneString) {
    return '';
  }

  const cleaned = phoneString.replace(/\D/g, '');
  const matches = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/);

  if (!matches) {
    return null;
  }

  const formatted = `(${matches[1]}) ${matches[2]}-${matches[3]}`;
  return formatted;
};

const sanitizeKebab = input => input.replace(/-/g, ' ');

const EMPTY_STRING = '';

const STRING_TRUE = 'true';

const toHashCode = str => {
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash; // Convert to 32bit integer
  }

  return hash;
};

const comparator = (a, b) => {
  if (a < b) {
    return -1;
  } else if (a > b) {
    return 1;
  }

  return 0;
};

const fromSnakeCaseToSpaces = str => str.split('_').join(' ');

const isString = str => typeof str === 'string' || str instanceof String;

const removeParentheses = str => str && String(str).replace(/[()]/g, '');

const isVowel = letter => ['a', 'e', 'i', 'o', 'u'].includes(letter && letter.toLowerCase());

const addAorAnToNoun = noun => (isVowel(noun && noun[0]) ? `an ${noun}` : `a ${noun}`);

const fromKebabToCamel = str =>
  str.split('-').reduce((acc, cur, idx) => `${acc}${idx === 0 ? cur : capitalize(cur)}`);

const sentenceToKebab = str =>
  str
    .toLowerCase()
    .split(' ')
    .join('-');

const replaceCharactersBeginningAtIndex = (string, replacementString, replacementStartingIndex) => {
  const leadingString = string.substring(0, replacementStartingIndex);
  const trailingString = string.substring(leadingString.length + replacementString.length);

  return leadingString + replacementString + trailingString;
};

const isSolvIdHash = str => /^[a-z0-9]{6,}$/i.test(str);

export {
  comparator,
  isString,
  trimTo,
  setCharAt,
  queryStringFromObject,
  isEmptyString,
  leftPad,
  numberWithCommas,
  formatPrice,
  formatPercentage,
  pluralize,
  arrayToEnglish,
  objectToEnglish,
  stripNonNumeric,
  getNameInitialsFromFullName,
  capitalize,
  capitalCase,
  isNumericString,
  formObjectToSlackPost,
  splitFullName,
  camelToSnake,
  isValidZipCode,
  sentenceCase,
  firstNCharacters,
  toUpperCase,
  formatPhone,
  sanitizeKebab,
  EMPTY_STRING,
  STRING_TRUE,
  toHashCode,
  fromSnakeCaseToSpaces,
  removeParentheses,
  isVowel,
  addAorAnToNoun,
  fromKebabToCamel,
  sentenceToKebab,
  replaceCharactersBeginningAtIndex,
  isSolvIdHash,
};
